import React, { useState } from 'react';
import { Button } from './BaseComponents';
import { BaseModal } from './BaseModal';
import { DifficultySelector } from './DifficultySelector';
import { DifficultyLevel } from '../types';

export interface QuizGenerationParams {
  questionCount: number;
  difficulty: DifficultyLevel;
}

export interface QuizGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (params: QuizGenerationParams) => void;
  isGenerating?: boolean;
  contentTitle?: string;
}

/**
 * QuizGenerationModal provides an interface for users to customize quiz parameters
 * before generation, including question count and difficulty level
 */
export const QuizGenerationModal: React.FC<QuizGenerationModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating = false,
  contentTitle = 'Generate Quiz'
}) => {
  const questionCount = 10;
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('medium');

  const handleGenerate = () => {
    onGenerate({
      questionCount,
      difficulty
    });
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} showCloseButton={false}>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="mx-auto icon-circle-lg bg-primary-50 mb-4">
          <svg className="h-8 w-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">{contentTitle}</h3>
        {contentTitle !== 'Generate Quiz' && (
          <p className="text-sm text-neutral-600 mb-4">
            Customize your quiz settings below
          </p>
        )}
      </div>


      {/* Difficulty Selection */}
      <DifficultySelector
        value={difficulty}
        onChange={setDifficulty}
        className="mb-6"
      />

      {/* Action Buttons */}
      <div className="flex flex-col space-y-3">
        <Button
          onClick={handleGenerate}
          variant="primary"
          fullWidth={true}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <div className="flex items-center justify-center">
              <div className="loading-spinner mr-2"></div>
              Generating Quiz...
            </div>
          ) : (
            'Generate Quiz'
          )}
        </Button>

        <Button
          onClick={onClose}
          variant="outline"
          fullWidth={true}
          disabled={isGenerating}
        >
          Cancel
        </Button>
      </div>
    </BaseModal>
  );
};